import { type VToken, getBlockTimeByChainId, vTokens } from '@endure/chains';
import BigNumber from 'bignumber.js';
import { COMPOUND_MANTISSA } from 'constants/compoundMantissa';
import { legacyPoolComptrollerAbi, resilientOracleAbi, vBep20Abi } from 'libs/contracts';
import { VError } from 'libs/errors';
import type { ChainId } from 'types';
import type { Address, PublicClient } from 'viem';
import type { ApiMarket, ApiPool, ApiTokenPrice } from '../getApiPools';
import { type MulticallResult, unwrapMulticallResult } from '../unwrapMulticallResult';

interface GetLocalPoolsInput {
  chainId: ChainId;
  publicClient: PublicClient;
  legacyPoolComptrollerContractAddress?: Address;
  resilientOracleContractAddress?: Address;
}

type MarketState = readonly [
  isListed: boolean,
  collateralFactorMantissa: bigint,
  isVenus: boolean,
  liquidationThresholdMantissa: bigint,
  liquidationIncentiveMantissa: bigint,
  marketPoolId: bigint,
  isBorrowAllowed: boolean,
];

type LocalMarketMulticallResults = readonly [
  MulticallResult<MarketState>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
  MulticallResult<bigint>,
];

type LocalMarketContract = {
  abi: typeof legacyPoolComptrollerAbi | typeof resilientOracleAbi | typeof vBep20Abi;
  address: Address;
  functionName: string;
  args?: readonly [Address];
};

const zeroMantissa = '0';

const toMantissaString = (value: bigint | number | string) => value.toString();

const toAprPercentage = ({
  chainId,
  ratePerBlock,
}: {
  chainId: ChainId;
  ratePerBlock: bigint;
}) => {
  const { blocksPerDay = 0 } = getBlockTimeByChainId({ chainId }) ?? {};

  return new BigNumber(ratePerBlock.toString())
    .div(COMPOUND_MANTISSA)
    .times(blocksPerDay)
    .times(365)
    .times(100)
    .toString();
};

const readComptroller = <T>({
  publicClient,
  comptrollerAddress,
  functionName,
  args,
}: {
  publicClient: PublicClient;
  comptrollerAddress: Address;
  functionName: string;
  args?: unknown[];
}) =>
  publicClient.readContract({
    abi: legacyPoolComptrollerAbi,
    address: comptrollerAddress,
    functionName,
    args,
  } as never) as Promise<T>;

export const getLocalPools = async ({
  chainId,
  publicClient,
  legacyPoolComptrollerContractAddress,
  resilientOracleContractAddress,
}: GetLocalPoolsInput) => {
  if (!legacyPoolComptrollerContractAddress || !resilientOracleContractAddress) {
    throw new VError({
      type: 'unexpected',
      code: 'somethingWentWrong',
      data: {
        exception: 'Missing local lending contract addresses',
      },
    });
  }

  const knownVTokens: VToken[] = vTokens[chainId] ?? [];

  const listedVTokenAddresses = await readComptroller<Address[]>({
    publicClient,
    comptrollerAddress: legacyPoolComptrollerContractAddress,
    functionName: 'getAllMarkets',
  });

  const listedVTokens = listedVTokenAddresses.flatMap((listedVTokenAddress) => {
    const vToken = knownVTokens.find(
      (knownVToken) => knownVToken.address.toLowerCase() === listedVTokenAddress.toLowerCase(),
    );

    return vToken ? [vToken] : [];
  });

  const localMarketContracts: LocalMarketContract[] = [];

  for (const vToken of listedVTokens) {
    localMarketContracts.push(
      {
        abi: legacyPoolComptrollerAbi,
        address: legacyPoolComptrollerContractAddress,
        functionName: 'markets',
        args: [vToken.address],
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'exchangeRateStored',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'supplyRatePerBlock',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'borrowRatePerBlock',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'reserveFactorMantissa',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'totalBorrows',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'totalSupply',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'getCash',
      },
      {
        abi: vBep20Abi,
        address: vToken.address,
        functionName: 'totalReserves',
      },
      {
        abi: legacyPoolComptrollerAbi,
        address: legacyPoolComptrollerContractAddress,
        functionName: 'borrowCaps',
        args: [vToken.address],
      },
      {
        abi: legacyPoolComptrollerAbi,
        address: legacyPoolComptrollerContractAddress,
        functionName: 'supplyCaps',
        args: [vToken.address],
      },
      {
        abi: resilientOracleAbi,
        address: resilientOracleContractAddress,
        functionName: 'getUnderlyingPrice',
        args: [vToken.address],
      },
    );
  }

  const localMarketResponses = (await publicClient.multicall({
    contracts: localMarketContracts as never,
  })) as unknown as MulticallResult<unknown>[];

  const markets = listedVTokens.map((vToken, marketIndex) => {
    const [
      marketStateResult,
      exchangeRateMantissaResult,
      supplyRatePerBlockResult,
      borrowRatePerBlockResult,
      reserveFactorMantissaResult,
      totalBorrowsMantissaResult,
      totalSupplyMantissaResult,
      cashMantissaResult,
      totalReservesMantissaResult,
      borrowCapsMantissaResult,
      supplyCapsMantissaResult,
      underlyingPriceMantissaResult,
    ] = localMarketResponses.slice(
      marketIndex * 12,
      marketIndex * 12 + 12,
    ) as unknown as LocalMarketMulticallResults;

    const marketState = unwrapMulticallResult(marketStateResult);
    const exchangeRateMantissa = unwrapMulticallResult(exchangeRateMantissaResult);
    const supplyRatePerBlock = unwrapMulticallResult(supplyRatePerBlockResult);
    const borrowRatePerBlock = unwrapMulticallResult(borrowRatePerBlockResult);
    const reserveFactorMantissa = unwrapMulticallResult(reserveFactorMantissaResult);
    const totalBorrowsMantissa = unwrapMulticallResult(totalBorrowsMantissaResult);
    const totalSupplyMantissa = unwrapMulticallResult(totalSupplyMantissaResult);
    const cashMantissa = unwrapMulticallResult(cashMantissaResult);
    const totalReservesMantissa = unwrapMulticallResult(totalReservesMantissaResult);
    const borrowCapsMantissa = unwrapMulticallResult(borrowCapsMantissaResult);
    const supplyCapsMantissa = unwrapMulticallResult(supplyCapsMantissaResult);
    const underlyingPriceMantissa = unwrapMulticallResult(underlyingPriceMantissaResult);

    const [
      isListed,
      collateralFactorMantissa,
      ,
      liquidationThresholdMantissa,
      liquidationIncentiveMantissa,
      ,
      isBorrowAllowed,
    ] = marketState;

    const market: ApiMarket = {
      address: vToken.address,
      symbol: vToken.symbol,
      name: vToken.symbol,
      underlyingAddress: vToken.underlyingToken.address,
      underlyingName: vToken.underlyingToken.symbol,
      underlyingSymbol: vToken.underlyingToken.symbol,
      underlyingDecimal: vToken.underlyingToken.decimals,
      borrowerDailyXvsMantissa: null,
      supplierDailyXvsMantissa: null,
      xvsBorrowIndex: null,
      xvsSupplyIndex: null,
      borrowRatePerBlock: toMantissaString(borrowRatePerBlock),
      supplyRatePerBlock: toMantissaString(supplyRatePerBlock),
      exchangeRateMantissa: toMantissaString(exchangeRateMantissa),
      totalBorrowsMantissa: toMantissaString(totalBorrowsMantissa),
      totalSupplyMantissa: toMantissaString(totalSupplyMantissa),
      cashMantissa: toMantissaString(cashMantissa),
      totalReservesMantissa: toMantissaString(totalReservesMantissa),
      reserveFactorMantissa: toMantissaString(reserveFactorMantissa),
      collateralFactorMantissa: toMantissaString(collateralFactorMantissa),
      liquidationThresholdMantissa: toMantissaString(liquidationThresholdMantissa),
      liquidationIncentiveMantissa: toMantissaString(liquidationIncentiveMantissa),
      borrowApy: toAprPercentage({ chainId, ratePerBlock: borrowRatePerBlock }),
      supplyApy: toAprPercentage({ chainId, ratePerBlock: supplyRatePerBlock }),
      borrowXvsApr: null,
      supplyXvsApr: null,
      liquidityCents: zeroMantissa,
      tokenPriceCents: zeroMantissa,
      totalDistributedMantissa: null,
      lastCalculatedXvsAccruedBlockNumber: null,
      supplyCapsMantissa: toMantissaString(supplyCapsMantissa),
      borrowCapsMantissa: toMantissaString(borrowCapsMantissa),
      badDebtMantissa: zeroMantissa,
      estimatedPrimeBorrowApyBoost: null,
      estimatedPrimeSupplyApyBoost: null,
      pausedActionsBitmap: 0,
      isListed,
      supplierCount: 0,
      borrowerCount: 0,
      poolComptrollerAddress: legacyPoolComptrollerContractAddress,
      rewardsDistributors: [],
      pointsDistributions: [],
      isBorrowable: isBorrowAllowed,
    };

    const price: ApiTokenPrice = {
      tokenWrappedAddress: null,
      priceMantissa: toMantissaString(underlyingPriceMantissa),
      priceSource: 'oracle',
      priceOracleAddress: resilientOracleContractAddress,
      isPriceInvalid: false,
      hasErrorFetchingPrice: false,
    };

    return {
      market,
      tokenAddress: vToken.underlyingToken.address.toLowerCase(),
      price,
    };
  });

  const validMarkets = markets.filter((market): market is NonNullable<typeof market> => !!market);

  // Local deployments currently use MockResilientOracle, so these prices are fixed seed prices.
  const tokenPricesMapping = validMarkets.reduce<Record<string, ApiTokenPrice[]>>(
    (acc, { tokenAddress, price }) => {
      acc[tokenAddress] = [price];

      return acc;
    },
    {},
  );

  const pool: ApiPool = {
    address: legacyPoolComptrollerContractAddress,
    name: 'Core',
    markets: validMarkets.map(({ market }) => market),
    priceOracleAddress: resilientOracleContractAddress,
    eModeGroups: [],
  };

  return {
    pools: [pool],
    tokenPricesMapping,
  };
};
